import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Grid,
  TextField,
  IconButton,
  Collapse,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography
} from '@material-ui/core'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useSchedule } from '../../../../hooks/ScheduleContext'
import { getCurrentThemeStyle } from '../../../../services/functions'

const { colors } = getCurrentThemeStyle()

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    width: '100%',
    marginTop: '16px',
  },
  filterPaper: {
    padding: '12px 16px',
    borderRadius: '8px',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E9ECEF',
    background: '#FFFFFF',
  },
  toggleButton: {
    background: colors.scale[400], // colors.scale[400]
    color: colors.background, // colors.background
    borderRadius: '4px',
    padding: '6px 12px',
    minWidth: '100px',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'none',
    border: 'none',
    height: '36px',
    cursor: 'pointer',
    transition: 'background 150ms linear',
    '&:hover': {
      background: colors.scale[500], // darken scale[500]
    },
    '&:active': {
      transform: 'scale(0.97)',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '14px',
      height: '40px',
      '& fieldset': {
        borderColor: '#E9ECEF',
      },
      '&:hover fieldset': {
        borderColor: colors.scale[400],
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.scale[400],
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      '&.Mui-focused': {
        color: colors.scale[400],
      },
    },
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'center',
  },
  searchButton: {
    background: colors.success, // colors.success
    color: '#FFFFFF',
    borderRadius: '4px',
    width: '40px',
    height: '40px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 150ms linear',
    '&:hover': {
      background: '#59B965',
      transform: 'scale(0.97)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  clearButton: {
    background: colors.gray[400], // colors.gray[400]
    color: '#FFFFFF',
    borderRadius: '4px',
    width: '40px',
    height: '40px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 150ms linear',
    '&:hover': {
      background: '#8F8888',
      transform: 'scale(0.97)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '8px',
  },
  filterTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#495057',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '8px',
  },
  selectField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '14px',
      height: '40px',
      '& fieldset': {
        borderColor: '#E9ECEF',
      },
      '&:hover fieldset': {
        borderColor: colors.scale[400],
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.scale[400],
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      transform: 'translate(14px, 12px) scale(1)',
      '&.Mui-focused, &.MuiInputLabel-shrink': {
        color: colors.scale[400],
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
  },
  divider: {
    margin: '12px 0',
    backgroundColor: '#E9ECEF',
  },
  filterSection: {
    marginBottom: '12px',
  },
}))

export function ScheduleFilter({ onFilter, onClear }) {
  const classes = useStyles()
  const { register, handleSubmit, reset, watch } = useForm()
  const { updateFilter } = useSchedule()
  const [isExpanded, setIsExpanded] = useState(false)

  // Recuperar filtros salvos do localStorage
  const savedFilters = JSON.parse(localStorage.getItem('@PETLAND/filerOptions')) || {}

  // Estados para filtros da agenda
  const [agendaOptions, setAgendaOptions] = useState({
    agenda: savedFilters.agenda || 'agenda',
    profissionais: savedFilters.profissionais || 'false',
    agendamentos: savedFilters.agendamentos || 'false',
    status: savedFilters.status || '*',
  })

  const watchedValues = watch()
  const hasFilters = Object.values(watchedValues).some(value => value && value.trim() !== '') ||
    agendaOptions.profissionais !== 'false' ||
    agendaOptions.agendamentos !== 'false' ||
    agendaOptions.status !== '*'

  const handleFilter = (data) => {
    // Remove campos vazios dos filtros de cliente/pet
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value && value.trim() !== '')
    )

    // Aplicar filtros de cliente/pet
    onFilter?.(filteredData)

    // Aplicar filtros da agenda
    updateFilter({
      employeesWorkingOnly: agendaOptions.profissionais === 'true',
      statusFilter: agendaOptions.status,
      visualByAppointments: agendaOptions.agendamentos === 'true',
    })

    // Salvar no localStorage
    localStorage.setItem('@PETLAND/filerOptions', JSON.stringify(agendaOptions))
  }

  const handleClear = () => {
    reset()
    setAgendaOptions({
      agenda: 'agenda',
      profissionais: 'false',
      agendamentos: 'false',
      status: '*',
    })

    // Limpar filtros da agenda
    updateFilter({
      employeesWorkingOnly: false,
      statusFilter: '*',
      visualByAppointments: false,
    })

    // Limpar localStorage
    localStorage.setItem('@PETLAND/filerOptions', JSON.stringify({
      agenda: 'agenda',
      profissionais: 'false',
      agendamentos: 'false',
      status: '*',
    }))

    onClear?.()
  }

  const handleAgendaOptionChange = (field, value) => {
    setAgendaOptions(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div className={classes.filterContainer}>
      <div className={classes.filterHeader}>
        <IconButton
          className={classes.toggleButton}
          onClick={toggleExpanded}
          size="small"
        >
          <FilterIcon />
          {isExpanded ? 'Ocultar Filtros' : 'Filtrar Agenda'}
        </IconButton>

        {hasFilters && !isExpanded && (
          <Tooltip title="Filtros ativos">
            <div style={{
              background: colors.scale[400],
              color: '#fafafa',
              borderRadius: '12px',
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              Filtros ativos
            </div>
          </Tooltip>
        )}
      </div>

      <Collapse in={isExpanded}>
        <Paper className={classes.filterPaper}>
          <form onSubmit={handleSubmit(handleFilter)}>
            {/* Seção: Filtros de Cliente/Pet */}
            <div className={classes.filterSection}>
              <Typography className={classes.sectionTitle}>
                Filtros de Cliente e Pet
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    size="small"
                    name="cpf"
                    label="CPF"
                    variant="outlined"
                    inputRef={register}
                    placeholder="000.000.000-00"
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    size="small"
                    name="cellphone"
                    label="Telefone"
                    variant="outlined"
                    inputRef={register}
                    placeholder="(00) 00000-0000"
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    size="small"
                    name="name"
                    label="Nome do Cliente"
                    variant="outlined"
                    inputRef={register}
                    placeholder="Digite o nome..."
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    size="small"
                    name="pet"
                    label="Nome do Pet"
                    variant="outlined"
                    inputRef={register}
                    placeholder="Digite o nome..."
                  />
                </Grid>
              </Grid>
            </div>

            <Divider className={classes.divider} />

            {/* Seção: Filtros da Agenda */}
            <div className={classes.filterSection}>
              <Typography className={classes.sectionTitle}>
                Filtros da Agenda
              </Typography>
              <Grid container spacing={2} alignItems="center">
                {/* Filtro de Profissionais */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth className={classes.selectField} size="small">
                    <InputLabel shrink={!!agendaOptions.profissionais}>Profissionais</InputLabel>
                    <Select
                      value={agendaOptions.profissionais}
                      onChange={(e) => handleAgendaOptionChange('profissionais', e.target.value)}
                      label="Profissionais"
                      variant="outlined"
                      displayEmpty
                    >
                      <MenuItem value="false">Todos os profissionais</MenuItem>
                      <MenuItem value="true">Trabalhando no dia</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Filtro de Visualização */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth className={classes.selectField} size="small">
                    <InputLabel shrink={!!agendaOptions.agendamentos}>Visualização</InputLabel>
                    <Select
                      value={agendaOptions.agendamentos}
                      onChange={(e) => handleAgendaOptionChange('agendamentos', e.target.value)}
                      label="Visualização"
                      variant="outlined"
                      displayEmpty
                    >
                      <MenuItem value="false">Por serviços</MenuItem>
                      <MenuItem value="true">Por agendamentos</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Filtro de Status */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth className={classes.selectField} size="small">
                    <InputLabel shrink={!!agendaOptions.status}>Status</InputLabel>
                    <Select
                      value={agendaOptions.status}
                      onChange={(e) => handleAgendaOptionChange('status', e.target.value)}
                      label="Status"
                      variant="outlined"
                      displayEmpty
                    >
                      <MenuItem value="*">Todos os status</MenuItem>
                      <MenuItem value="Agendado">Agendado</MenuItem>
                      <MenuItem value="Em andamento">Em andamento</MenuItem>
                      <MenuItem value="Finalizado">Finalizado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Botões de Ação */}
                <Grid item xs={12} sm={3}>
                  <div className={classes.actionButtons}>
                    <Tooltip title="Aplicar filtros">
                      <IconButton
                        className={classes.searchButton}
                        type="submit"
                        size="small"
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Limpar todos os filtros">
                      <IconButton
                        className={classes.clearButton}
                        onClick={handleClear}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Grid>
              </Grid>
            </div>
          </form>
        </Paper>
      </Collapse>
    </div>
  )
}
